import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Autocomplete,
  Button,
  Divider,
  Grid,
  InputBase,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useForm } from "../../../hooks";
import { NumberInput } from "../NumberInput";
import { createReservation } from "../../helpers/reservation.js";
import { useState } from "react";

export const AddReservation = ({
  userList,
  setUserId,
  companiesList,
  vehiclesList,
  userId,
}) => {
  const { t } = useTranslation("global");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const companyListWithoutId = userList.map((user) => user.name);
  const [buttonDisabled, setButtonDisabled] = useState(false);

  const [formValues, handleInputChange, reset] = useForm({
    intention: "No",
    amount: 0,
    whoInvests: "",
    comments: "",
    vehicle: vehiclesList[0],
    investor: "",
  });

  const { intention, amount, whoInvests, comments, vehicle, investor } =
    formValues;

  const onSubmit = (e) => {
    e.preventDefault();
    setButtonDisabled(true);
    const intentionBBDD =
      intention === "Si" ? "Reserva realizada" : "No reserva";
    const who = intention === "No" ? investor : whoInvests;
    const create = createReservation(
      {
        ...formValues,
        intention: intentionBBDD,
        individualId: userId,
        opportunityId: searchParams.get("opportunity"),
        whoInvests: who,
      },
      t("reservation.successMsgAdd"),
      t("reservation.errorMsg"),
      dispatch
    );
    create && navigate(-1);
  };

  const handleCancel = () => {
    reset();
    navigate(-1);
  };

  const changeInvestor = (inv) => {
    const id = userList.filter((user) => user.name === inv.target.value)[0]?.id;
    setUserId(id);
    handleInputChange({
      target: { name: "investor", value: inv.target.value },
    });
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography color="primary" variant="h5">
          {t("reservation.addReservation")}
        </Typography>
      </Stack>
      <Divider sx={{ marginTop: 2, marginBottom: 3 }} />
      <form
        onSubmit={onSubmit}
        style={{
          padding: "10px 5px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-around",
        }}
      >
        <Stack
          sx={{
            height: "calc(70vh - 200px)",
            overflowY: "auto",
            paddingBottom: "10px",
            paddingRight: "20px",
          }}
        >
          <Grid container direction="row" width="100%">
            <Grid item xs={12} padding="0 5px">
              <Typography variant="overline" color="secondary">
                {t("reservation.investor")}
              </Typography>
              <Autocomplete
                disablePortal
                options={[...companyListWithoutId, ""]}
                size="small"
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" name="investor" />
                )}
                value={investor}
                onSelect={(inv) => changeInvestor(inv)}
                required
              />
            </Grid>
          </Grid>

          {investor !== "" && (
            <>
              <Grid container direction="row" width="100%">
                <Grid item xs={12} padding="0 5px">
                  <Typography variant="overline" color="secondary">
                    {t("reservation.intention")}
                  </Typography>
                  <Autocomplete
                    disablePortal
                    options={["Si", "No"]}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        name="intention"
                      />
                    )}
                    value={intention}
                    onSelect={handleInputChange}
                    required
                  />
                </Grid>
              </Grid>

              {intention === "Si" && (
                <>
                  <Grid container direction="row" width="100%">
                    <Grid item xs={12} padding="0 5px">
                      <Typography variant="overline" color="secondary">
                        {t("reservation.whoInvests")}
                      </Typography>
                      <Autocomplete
                        disablePortal
                        options={[...companiesList, investor]}
                        size="small"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            name="whoInvests"
                          />
                        )}
                        value={whoInvests}
                        onSelect={handleInputChange}
                        required
                      />
                    </Grid>
                  </Grid>

                  <Grid container direction="row" width="100%">
                    <Grid item xs={12} padding="0 5px">
                      <NumberInput
                        name={amount}
                        title="amount"
                        handleInputChange={handleInputChange}
                        min={2000}
                        required={true}
                      />
                    </Grid>
                  </Grid>

                  <Grid container direction="row" width="100%">
                    <Grid item xs={12} padding="0 5px">
                      <Typography variant="overline" color="secondary">
                        {t("addevent.comments")}
                      </Typography>
                      <InputBase
                        fullWidth
                        name="comments"
                        size="small"
                        variant="standard"
                        value={comments}
                        onChange={handleInputChange}
                        type="text"
                        multiline
                      />
                    </Grid>
                  </Grid>

                  <Stack direction="column" width="100%" gap={0}>
                    <Typography variant="overline" color="secondary">
                      {t("opportunities.vehicle")}
                    </Typography>
                    <Autocomplete
                      disablePortal
                      options={vehiclesList}
                      size="small"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          name="vehicle"
                        />
                      )}
                      value={vehicle}
                      onSelect={handleInputChange}
                      fullWidth
                      required
                    />
                  </Stack>
                </>
              )}
            </>
          )}
        </Stack>

        <Stack direction="row" justifyContent="end" gap={2} marginTop={3}>
          <Button
            sx={{ width: "140px" }}
            color="secondary"
            onClick={handleCancel}
            variant="outlined"
          >
            {t("myprofile.cancelButton")}
          </Button>
          <Button
            sx={{ width: "140px" }}
            type="submit"
            disabled={buttonDisabled}
            variant="contained"
          >
            {t("myprofile.confirmButton")}
          </Button>
        </Stack>
      </form>
    </>
  );
};
